import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { loadTodosForUrgencyAsync } from "store/actions/todoActions";
import { setTodoListNavigation } from "store/actions/todoNavigationActions";
//#endregion
//#region types
import { FormFieldConstants, IconDefinitionConstants, ServiceConstants, TodoConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { ExpandableListItem, TableContext } from "components/common";
import { useLocation, useNavigate } from "react-router";
import useTodosByUrgency from "./useTodosByUrgency";
import useTodoGroupingHelpers from "./useTodoGroupingHelpers";
import TodoChipsList from "./chipsList";
import useTodoReportFilterDefinition from "components/todos/forms/useReportFilterDefinition";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import { TodoService } from "services";

const TodosByUrgency: React.FC = () => {
  const [expanded, setExpanded] = React.useState<string | undefined>();
  const [selectedUrgency, setUrgency] = React.useState<WintSuperkollAPIModel.TodoUrgency | undefined>();
  const dispatch = useDispatch();

  const { todoSummary, getMockApiResponse } = useTodoGroupingHelpers();
  const { todosByUrgency: todoItems } = useTodosByUrgency();

  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const defaultExpansion =
      todoSummary?.todosUrgencySummary?.length === 1
        ? todoSummary.todosUrgencySummary[0].todoUrgency
        : WintSuperkollAPIModel.TodoUrgency.Overdue;
    onExpanded(defaultExpansion, defaultExpansion.toString());
  }, [todoSummary]);

  const onExpanded = (urgency: WintSuperkollAPIModel.TodoUrgency, panel?: string) => {
    setExpanded(panel);
    setUrgency(urgency);
  };

  const goToAllTodosByUrgency = async (urgency: WintSuperkollAPIModel.TodoUrgency) => {
    navigate(UrlHelper.formatPath(`/Reports/AllToDos?${FormFieldConstants.Todo.Filter.Urgency}=${urgency}`), {
      state: { background: location },
    });
  };

  const loadTodoItemsIfMissing = (urgency: WintSuperkollAPIModel.TodoUrgency) => {
    const selectedUrgencyItem = todoItems.get(urgency);
    if (!selectedUrgencyItem) return;
    if (!selectedUrgencyItem.fullyLoaded) {
      dispatch(loadTodosForUrgencyAsync(urgency, selectedUrgencyItem.total));
    }
  };

  const { filterQueryStateHandler } = useTodoReportFilterDefinition();

  const onGoToItemClick = async (urgency: WintSuperkollAPIModel.TodoUrgency, index: number) => {
    setUrgency(urgency);
    dispatch(
      setTodoListNavigation({
        indexToOpen: index,
        pageToOpen: 0,
        numPerPage: todoItems.get(urgency)?.total ?? 0,
        loadNext: () => {
          return drawerFilterFunc(urgency).then((res) => ({
            items: res?.data.items ?? [],
            totalItems: res?.data.totalItems ?? 0,
          }));
        },
        todoCanIncludeInNavigation: (todo: WintSuperkollAPIModel.Todo) => todo.urgency === urgency,
      })
    );

    const urlParams: URLSearchParams = new URLSearchParams();
    filterQueryStateHandler.urgency?.urlEncode(urlParams, undefined, [{ id: urgency, value: "" }]);
    filterQueryStateHandler.snoozedOnly?.urlEncode(urlParams, undefined, false);

    navigate(`${UrlHelper.formatPath(`/Todo/${index}`)}?${urlParams.toString()}`, {
      state: { background: location },
    });
  };

  React.useEffect(() => {
    if (selectedUrgency === undefined) return;
    loadTodoItemsIfMissing(selectedUrgency);
  }, [todoItems, selectedUrgency]);

  const drawerFilterFunc = React.useCallback(
    async (selectedUrgency: WintSuperkollAPIModel.TodoUrgency) => {
      const selectedUrgencyItems = todoItems.get(selectedUrgency);
      if (selectedUrgencyItems?.fullyLoaded) {
        return getMockApiResponse((selectedUrgencyItems?.items ?? []) as WintSuperkollAPIModel.Todo[]);
      } else {
        return await TodoService.getTodosByFilter({
          ...ServiceConstants.DefaultFilter,
          urgency: [selectedUrgency],
          types: TodoConstants.SupportingTodoTypes,
          page: 0,
          numPerPage: (selectedUrgencyItems?.total ?? 0) + 10,
          excludeSnoozed: true,
        }).then((res) => res.result);
      }
    },
    [todoItems]
  );

  return todoSummary === undefined ? (
    <></>
  ) : (
    <TableContext
      tableKey={"todosByUrgency"}
      filter={{
        ...ServiceConstants.DefaultFilter,
        orderByProperty: "Urgency",
        numPerPage: TodoConstants.TodosPerPage,
        orderByDescending: true,
      }}
    >
      <Grid container rowGap={1}>
        {todoSummary.todosUrgencySummary
          ?.sort((a, b) => b.todoUrgency - a.todoUrgency)
          .map((item) =>
            item.todosCount ? (
              <ExpandableListItem
                key={item.todoUrgency}
                id={item.todoUrgency.toString()}
                title={LocalizationHelperInstance.getTranslatedEnumValue("Todos.Urgency", item.todoUrgency)}
                headerSubContent={`${item.todosCount} ${LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Common.Pc
                )}`}
                headerHoverActions={[
                  {
                    icon: IconDefinitionConstants.WEmpty,
                    description: `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.View)} 1/${
                      item.todosCount
                    }`,
                    onClickHandler: onGoToItemClick.bind(null, item.todoUrgency, 0),
                  },
                  {
                    icon: IconDefinitionConstants.WEmpty,
                    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ViewAll),
                    onClickHandler: goToAllTodosByUrgency.bind(null, item.todoUrgency),
                    showOnExpand: true,
                  },
                ]}
                expanded={expanded?.toString()}
                onChange={onExpanded.bind(null, item.todoUrgency)}
              >
                <TodoChipsList
                  todoItems={todoItems.get(item.todoUrgency)?.items?.slice(0, 10)}
                  total={todoItems.get(item.todoUrgency)?.total}
                  loadingItemsCount={item.todosCount}
                  onGoToItemClick={onGoToItemClick.bind(null, item.todoUrgency)}
                  goToAllTodosByType={goToAllTodosByUrgency.bind(null, item.todoUrgency)}
                />
              </ExpandableListItem>
            ) : (
              <React.Fragment key={item.todoUrgency} />
            )
          )}
      </Grid>
    </TableContext>
  );
};

export default TodosByUrgency;
